import { Box, BoxProps } from '@chakra-ui/react'
import { forwardRef } from 'react'

/**
 * Tamanhos do ícone, como token e não como medida — a mesma ideia do `size` do `FormModal`.
 *
 * Havia **17 valores** escritos à mão no `style` dos `<i>`, número e string misturados
 * (`13` e `'14px'`, 1px de diferença, os dois mais comuns). `md` é esse par resolvido.
 */
export type IconSize = 'sm' | 'md' | 'lg' | 'xl'

const ICON_SIZES: Record<IconSize, string> = {
  sm: '12px',
  md: '14px',
  lg: '18px',
  xl: '24px'
}

export interface IIconProps extends Omit<BoxProps, 'children' | 'as'> {
  /**
   * A classe do LineIcons, inteira (`lni lni-trash-can`) — a mesma grafia que já chega nos
   * props `icon` do `StatCard`, do `EmptyState` e do `DetailField`, e que o concurso persiste
   * em `ART_FIELD_ICON_OPTIONS` e compartilha com o site público.
   */
  name: string
  /**
   * Sem `size` o ícone vale `1em` e acompanha o texto em que está — é o padrão do próprio
   * LineIcons (`.lni { font: … 1em/1 }`) e o que 137 dos 187 `<i>` crus já faziam. O token
   * nomeia só a saída deliberada dele.
   */
  size?: IconSize
  /**
   * Nome acessível, para o ícone que **é** o conteúdo do controle (um link que só tem ele
   * dentro). Sem `label` o ícone é decorativo e sai da árvore de acessibilidade — que é o
   * certo dentro de um botão que já tem `aria-label`.
   */
  label?: string
}

/**
 * Ícone do painel. Eram **280 usos em 86 arquivos e três grafias**: o `<i className="lni …">`
 * cru (187), o `<Box as="i" className="lni …">` (18) e o `className={`lni ${item.icon}`}` da
 * sidebar, que guardava o nome sem o prefixo — um terceiro dialeto para a mesma string.
 *
 * O que divergia não era só a grafia: **50 dos `<i>` carregavam `style={{ fontSize }}` à mão,
 * em 17 valores**, e três traziam cor em hexadecimal (`'#805AD5'`, `'#ED8936'`) dentro do
 * `style` — fora do tema, onde ninguém acha para trocar. Aqui o tamanho é token e a cor é
 * prop do Chakra (`color="purple.500"`), como em qualquer outro elemento do painel.
 *
 * O registry `ACTIONS` monta os ícones dele por aqui, então trocar de biblioteca de ícones
 * continua sendo editar um arquivo só.
 *
 * **O que deliberadamente fica de fora:** o `react-icons` do módulo `ArtworkEvaluation`, que
 * sai quando o módulo inteiro sair — converter só os ícones dele deixaria o módulo com metade
 * do vocabulário novo, que é a lição que o `docs/padronizacao-pendente.md` já registrou. Os
 * três SVG de `components/icons` (`TrophyIcon`, `SupportHeadsetIcon`, `AiSparkleIcon`) também
 * ficam: são glifos que **não existem** no LineIcons 4.0, e é por isso que foram desenhados.
 *
 * **Encaminha o ref** pelo mesmo motivo que o `SectionCard` e o `EmptyState`: o `<Tooltip>`
 * clona o filho e prende um ref nele para se posicionar. Sem isso o React avisava
 * "Function components cannot be given refs" e **o tooltip de um ícone simplesmente não
 * aparecia** — o que atinge as dicas de atraso da entrega de trabalho e todo `<Tooltip>` cujo
 * único filho é um ícone.
 */
const Icon = forwardRef<HTMLElement, IIconProps>(function Icon({ name, size, label, ...rest }, ref) {
  return (
    <Box
      ref={ref}
      as="i"
      className={name}
      fontSize={size ? ICON_SIZES[size] : undefined}
      {...(label ? { role: 'img', 'aria-label': label } : { 'aria-hidden': true })}
      {...rest}
    />
  )
})

export default Icon
